import React, { useState, useEffect, useRef } from 'react'
import useDebounce from '../../../hooks/useDebounce'
import './style.scss'

export default function SearchBar({
    value: externalValue,
    defaultValue = '',
    onChange,
    onSearch,
    onFocus,
    onBlur,
    placeholder = 'search...',
    debounceTime = 300,
    loading = false,
    disabled = false,
    autoFocus = false,
    size = 'medium',
    variant = 'outlined',
    prefixIcon,
    className = '',
    style = {},
    inputProps = {}
}) {
    const isControlled = externalValue !== undefined
    const [internalValue, setInternalValue] = useState(defaultValue)

    const currentValue = isControlled ? externalValue : internalValue

    const debouncedValue = useDebounce(currentValue, debounceTime)
    const isFirstRender = useRef(true)

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        if (debounceTime > 0 && onSearch) {
            onSearch(debouncedValue)
        }
    }, [debouncedValue])

    const handleChange = (e) => {
        const val = e.target.value
        if (!isControlled) {
            setInternalValue(val)
        }
        if (onChange) {
            onChange(val, e)
        }
        if ((!debounceTime || debounceTime <= 0) && onSearch) {
            onSearch(val)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && onSearch) {
            onSearch(currentValue)
        }
        if (inputProps.onKeyDown) {
            inputProps.onKeyDown(e)
        }
    }

    const containerClasses = [
        'search-wrap-common',
        size ? `search-size-${size}-common` : '',
        variant ? `search-variant-${variant}-common` : '',
        loading ? 'has-loading-common' : '',
        disabled ? 'search-disabled-common' : '',
        className
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <div className={containerClasses} style={style}>
            <span className="search-icon-common">
                {prefixIcon || (
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                )}
            </span>

            <input
                type="text"
                className="search-input-common"
                value={currentValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={onFocus}
                onBlur={onBlur}
                placeholder={placeholder}
                disabled={disabled}
                autoFocus={autoFocus}
                {...inputProps}
            />

            {loading && (
                <span className="search-spinner-common">
                    <svg
                        className="spinner-icon-common"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <line x1="12" y1="2" x2="12" y2="6" />
                        <line x1="12" y1="18" x2="12" y2="22" />
                        <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                        <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                        <line x1="2" y1="12" x2="6" y2="12" />
                        <line x1="18" y1="12" x2="22" y2="12" />
                        <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                        <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                    </svg>
                </span>
            )}
        </div>
    )
}
